import { useEffect, useState } from "react";
import { ResponsiveContainer, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Line, ComposedChart, Legend, ReferenceLine } from "recharts";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { addDays, addMonths, addYears, format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { nf } from "./shared";
import { ConsumiVesta } from "./ConsumiVesta";

const TABS = [["day", "Giorno"], ["month", "Mese"], ["year", "Anno"]];
const tip = { contentStyle: { background: "#0a0a0a", border: "1px solid #262626", fontSize: 12 }, labelStyle: { color: "#a3a3a3" } };
const C = { load: "#60a5fa", batt: "#22c55e", grid: "#c084fc", soc: "#facc15" };
const ax = { tick: { fontSize: 10, fill: "#737373" } };
const leg = { wrapperStyle: { fontSize: 11 }, iconSize: 8 };

const Bilancio = ({ b }) => (
  <div className="grid grid-cols-3 gap-2 text-[11px] sm:grid-cols-6" data-testid="plant-chart-bilancio">
    {[["Prodotta", b.produzione, "kWh"], ["Consumata", b.consumo, "kWh"], ["Immessa", b.immessa, "kWh"], ["Prelevata", b.prelevata, "kWh"],
      ...(b.senza_batteria ? [] : [["Batt. carica", b.carica, "kWh"], ["Batt. scarica", b.scarica, "kWh"]])].map(([l, v, u]) => (
      <div key={l} className="rounded-lg border border-neutral-800 px-2 py-1.5"><div className="text-neutral-500">{l}</div><div className="font-mono font-bold text-white">{v == null ? "non misurato" : `${nf(v)} ${u}`}</div></div>
    ))}
    {b.non_misurato && <div className="col-span-3 text-amber-400/90 sm:col-span-6" data-testid="plant-chart-no-meter">{b.senza_batteria ? "Il meter è sugli inverter di stringa, ma FIER non ne trasmette le letture: consumo, immissione e prelievo non sono misurabili, è disponibile solo la produzione." : "Il meter verso la rete non è letto da FIER: il consumo dell'utenza non è misurabile, sono disponibili solo produzione e batteria."}</div>}
    {b.autoconsumo_pct != null && <div className="col-span-3 text-neutral-400 sm:col-span-6">Autoconsumo <b className="text-white">{b.autoconsumo_pct}%</b> · Autosufficienza <b className="text-white">{b.autosufficienza_pct}%</b></div>}
  </div>
);

const DayChart = ({ d, accent }) => (
  <ComposedChart data={d.points}>
    <CartesianGrid stroke="#1f1f1f" vertical={false} />
    <XAxis dataKey="t" {...ax} interval="preserveStartEnd" minTickGap={28} />
    <YAxis yAxisId="w" {...ax} width={44} />
    {d.ibrido && <YAxis yAxisId="s" orientation="right" domain={[0, 100]} {...ax} width={30} />}
    <Tooltip {...tip} />
    <Legend {...leg} />
    <ReferenceLine yAxisId="w" y={0} stroke="#404040" />
    <Area yAxisId="w" type="monotone" dataKey="pv_w" name="FV (W)" stroke={accent} fill={accent} fillOpacity={0.25} dot={false} />
    {d.ibrido && <Area yAxisId="w" type="monotone" dataKey="load_w" name="Consumo (W)" stroke={C.load} fill={C.load} fillOpacity={0.1} dot={false} />}
    {d.ibrido && <Line yAxisId="w" type="monotone" dataKey="batt_w" name="Batteria (W, + scarica / − carica)" stroke={C.batt} dot={false} strokeWidth={1.5} />}
    {d.ibrido && <Line yAxisId="w" type="monotone" dataKey="grid_w" name="Rete (W, + prelievo / − immissione)" stroke={C.grid} dot={false} strokeWidth={1.5} />}
    {d.ibrido && <Line yAxisId="s" type="monotone" dataKey="soc" name="SOC (%)" stroke={C.soc} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />}
  </ComposedChart>
);

const PeriodChart = ({ d, accent, onPick }) => (
  <BarChart data={d.points} onClick={(e) => e && e.activeTooltipIndex != null && onPick(Number(e.activeTooltipIndex))} style={{ cursor: "pointer" }}>
    <CartesianGrid stroke="#1f1f1f" vertical={false} />
    <XAxis dataKey="t" {...ax} />
    <YAxis {...ax} width={44} />
    <Tooltip {...tip} cursor={{ fill: "#ffffff10" }} />
    {d.ibrido && <Legend {...leg} />}
    <Bar dataKey="kwh" name="Prodotta kWh" fill={accent} radius={[3, 3, 0, 0]} />
    {d.ibrido && <Bar dataKey="consumo" name="Consumata kWh" fill={C.load} radius={[3, 3, 0, 0]} />}
    {d.ibrido && <Bar dataKey="immessa" name="Immessa" stackId="r" fill={C.grid} />}
    {d.ibrido && <Bar dataKey="prelevata" name="Prelevata" stackId="r" fill="#7c3aed" />}
    {d.ibrido && <Bar dataKey="scarica" name="Batt. scarica" fill={C.batt} radius={[3, 3, 0, 0]} />}
  </BarChart>
);

const vuoto = (d, type) => !d.points?.length || !d.points.some((p) => (type === "day" ? p.pv_w || p.load_w || p.soc || p.batt_w || p.grid_w : p.kwh || p.consumo));
const iso = (x) => format(x, "yyyy-MM-dd");
const SHIFT = { day: addDays, month: addMonths, year: addYears };
const LABEL = { day: "EEE d MMM yyyy", month: "MMMM yyyy", year: "yyyy" };

function DateNav({ type, date, setDate, accent }) {
  const today = new Date(), cur = parseISO(date);
  const next = SHIFT[type](cur, 1);
  const canNext = type === "day" ? iso(next) <= iso(today) : type === "month" ? format(next, "yyyy-MM") <= format(today, "yyyy-MM") : next.getFullYear() <= today.getFullYear();
  const isToday = date === iso(today);
  const btn = "rounded-full border border-neutral-800 p-1.5 text-neutral-300 transition-colors hover:border-neutral-500 disabled:opacity-30";
  return (
    <div className="flex items-center gap-1.5" data-testid="plant-chart-datenav">
      <button type="button" className={btn} onClick={() => setDate(iso(SHIFT[type](cur, -1)))} data-testid="plant-chart-prev" aria-label="Precedente"><ChevronLeft className="h-3.5 w-3.5" /></button>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-neutral-800 px-3 py-1 text-xs font-semibold capitalize text-white transition-colors hover:border-neutral-500" data-testid="plant-chart-date">
            <CalendarDays className="h-3.5 w-3.5" style={{ color: accent }} />{format(cur, LABEL[type], { locale: it })}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto border-neutral-800 bg-neutral-950 p-0" align="start">
          <Calendar mode="single" locale={it} selected={cur} defaultMonth={cur} onSelect={(x) => x && setDate(iso(x))} disabled={{ after: today }} data-testid="plant-chart-calendar" />
        </PopoverContent>
      </Popover>
      <button type="button" className={btn} disabled={!canNext} onClick={() => setDate(iso(next))} data-testid="plant-chart-next" aria-label="Successivo"><ChevronRight className="h-3.5 w-3.5" /></button>
      {!isToday && <button type="button" onClick={() => setDate(iso(today))} data-testid="plant-chart-today"
        className="rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors hover:bg-white/5" style={{ borderColor: accent, color: accent }}>Oggi</button>}
    </div>
  );
}

function InverterPick({ list, sel, setSel, per, accent }) {
  if (!list || list.length < 2) return null;
  const kwh = Object.fromEntries((per || []).map((x) => [x.sn, x.kwh]));
  const on = (sn) => !sel.length || sel.includes(sn);
  const flip = (sn) => {
    if (!sel.length) { setSel([sn]); return; }           // da "Tutti": seleziona solo questo inverter
    const nx = sel.includes(sn) ? sel.filter((x) => x !== sn) : [...sel, sn];
    setSel(nx.length === list.length || !nx.length ? [] : nx);
  };
  const chip = (active) => `rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${active ? "text-black" : "border-neutral-800 text-neutral-400 hover:text-white"}`;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="plant-chart-inverters">
      <button type="button" onClick={() => setSel([])} className={chip(!sel.length)} style={!sel.length ? { background: accent, borderColor: accent } : undefined} data-testid="plant-chart-inv-all">Tutti</button>
      {list.map((i) => (
        <button key={i.sn} type="button" onClick={() => flip(i.sn)} data-testid={`plant-chart-inv-${i.sn}`} className={chip(sel.length && on(i.sn))}
          style={sel.length && on(i.sn) ? { background: accent, borderColor: accent } : undefined}>
          {i.model} ·{i.sn.slice(-5)}{kwh[i.sn] != null && on(i.sn) ? ` · ${nf(kwh[i.sn])} kWh` : ""}
        </button>
      ))}
    </div>
  );
}

export function PlantChart({ base, plantId, params, accent, inverters, ver }) {
  const [sel, setSel] = useState([]);
  const [type, setType] = useState("day");
  const [date, setDate] = useState(iso(new Date()));
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    setD(null);
    api.get(`${base}/plants/${encodeURIComponent(plantId)}/line`, { params: { ...params, type, date, ...(sel.length ? { sn: sel.join(",") } : {}) } })
      .then((r) => live && setD(r.data)).catch(() => live && setD({ points: [], totale_kwh: 0 }));
    return () => { live = false; };
  }, [base, plantId, type, date, sel.join(","), ver]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (i) => {
    const cur = parseISO(date);
    if (type === "month") { setDate(iso(new Date(cur.getFullYear(), cur.getMonth(), Number(d.points[i]?.t) || i + 1))); setType("day"); }
    else if (type === "year") { setDate(iso(new Date(cur.getFullYear(), i, 1))); setType("month"); }
  };
  return (
    <div className="pointer-events-auto space-y-3" data-testid="plant-chart">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-full border border-neutral-800 p-1">
          {TABS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setType(k)} data-testid={`plant-chart-tab-${k}`}
              className="rounded-full px-3 py-1 text-xs font-bold transition-colors" style={type === k ? { background: accent, color: "#000" } : { color: "#a3a3a3" }}>{l}</button>
          ))}
        </div>
        <DateNav type={type} date={date} setDate={setDate} accent={accent} />
        <div className="text-xs text-neutral-400" data-testid="plant-chart-total">Produzione: <b className="font-mono text-white">{nf(d?.totale_kwh)} kWh</b>
          {d?.completezza != null && <span className={`ml-2 ${d.completezza < 80 ? "text-amber-400" : "text-neutral-500"}`} data-testid="plant-chart-completezza">· dati ricevuti {d.completezza}%</span>}
          {d?.da_archivio && <span className="ml-2 text-neutral-600" data-testid="plant-chart-archivio">· archivio CRM{d.integrato_live ? " + FIER" : ""}</span>}
          {d?.fonte_totale === "stimato" && <span className="ml-2 text-amber-400" data-testid="plant-chart-stimato">· totale stimato dalla curva (contatore FIER non disponibile)</span>}
          {d?.giorni_stimati > 0 && <span className="ml-2 text-amber-400" data-testid="plant-chart-giorni-stimati">· {d.giorni_stimati} gg stimati dalla curva</span>}</div>
      </div>
      <InverterPick list={inverters} sel={sel} setSel={setSel} per={d?.per_inverter} accent={accent} />
      <div className="h-64 rounded-xl border border-neutral-800 bg-black/40 p-2">
        {!d ? <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" style={{ color: accent }} /></div> : vuoto(d, type) ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center" data-testid="plant-chart-empty">
            <p className="text-xs leading-relaxed text-neutral-400">{type === "day" ? "Il cloud FIER non ha registrato la curva a 5 minuti di questo giorno per l'inverter: arrivano solo i totali giornalieri." : "Il cloud FIER non ha dati per questo periodo."}</p>
            {type === "day" && <button type="button" onClick={() => setType("month")} data-testid="plant-chart-empty-month" className="rounded-full border px-3 py-1 text-[11px] font-bold" style={{ borderColor: accent, color: accent }}>Vedi i totali del mese</button>}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 240 }}>
            {type === "day" ? DayChart({ d, accent }) : PeriodChart({ d, accent, onPick: pick })}
          </ResponsiveContainer>
        )}
      </div>
      {type !== "day" && d?.points?.length > 0 && <p className="text-[11px] text-neutral-500">Tocca una barra per aprire {type === "month" ? "le curve di quel giorno" : "quel mese"}.</p>}
      {d?.consumi_vesta?.pubblicati ? <ConsumiVesta c={d.consumi_vesta} /> : <>
        {d?.bilancio && <Bilancio b={d.bilancio} />}
        {d?.consumi_vesta && base === "/monitoring" && <ConsumiVesta c={d.consumi_vesta} anteprima />}
      </>}
    </div>
  );
}
